import { get } from 'lodash-es';
import type { AiJsonLogicAssistOptions } from '../../types/schemas';

const USER_INPUT_PLACEHOLDER = 'userInput';
const ROOT_PATH_PREFIX = '$.';

const stringifyContextValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

interface BuildAgentChatMessageParams {
  userInput: string;
  aiAssistOptions: AiJsonLogicAssistOptions;
  entityRootData?: Record<string, unknown>;
  /**
   * The data for the current form section (tree node). Used to resolve
   * non-`$`-prefixed paths in `messageContext`, consistent with the
   * `x-entity-conditional` convention where unprefixed paths resolve
   * from the current object context.
   */
  formData?: Record<string, unknown>;
}

/**
 * Builds the final message to send to the agent chat API by resolving a
 * schema-configured `messageTemplate` with `{userInput}` and any
 * `messageContext` variable placeholders.
 *
 * Path resolution follows the same convention as `x-entity-conditional`:
 * - **`$` prefix** — resolved from entity root data (e.g. `"$.attributes"`)
 * - **No prefix** — resolved from the current form data (e.g. `"internalPageName"`)
 *
 * When no `messageTemplate` is configured the raw `userInput` is returned.
 */
export const buildAgentChatMessage = ({
  userInput,
  aiAssistOptions,
  entityRootData,
  formData,
}: BuildAgentChatMessageParams): string => {
  const { messageTemplate, messageContext } = aiAssistOptions;

  if (!messageTemplate) return userInput;

  const contextValues: Record<string, string> = {
    [USER_INPUT_PLACEHOLDER]: userInput,
  };

  if (messageContext) {
    Object.entries(messageContext).forEach(([key, dataPath]) => {
      const isRootPath = dataPath.startsWith(ROOT_PATH_PREFIX);
      const resolvedPath = isRootPath
        ? dataPath.slice(ROOT_PATH_PREFIX.length)
        : dataPath;
      const source = isRootPath ? entityRootData : formData;

      contextValues[key] = stringifyContextValue(get(source, resolvedPath));
    });
  }

  return messageTemplate.replace(
    /\{(\w+)\}/g,
    (match, key: string) => contextValues[key] ?? match,
  );
};
